import React, {
  lazy,
  Suspense,
  type FC,
  type ReactNode,
  useReducer,
  useState,
  useEffect,
  type PropsWithChildren,
} from 'react';

import styled from '@emotion/styled';
import {
  useSiteMetadata,
  useNavFooterMenu,
  useAnnouncementBanner,
  useGlobalNav,
  useSmartlingStrings,
} from '@lucid/hooks';
import { getLanguage, isBrowser } from '@lucid/utils';
import {
  AnnouncementBanner,
  Box,
  LayoutContext,
  Pingdom,
  SiteFooter,
  type SiteFooterOptionalProps,
  GlobalCSS,
  ThemeProvider,
  ABTest,
  Delayed,
  NavBlogProvider,
  Navbar,
  type NavbarOptionalProps,
} from '@lucid/components';
import { theme } from '@lucid/theme';

import type { Lucid } from 'types/types';
import { useDefaultPageUrls } from 'hooks/hooks';

const CMSNodeButton = lazy(() => import('@lucid/components').then(module => ({ default: module.CMSNodeButton })));
const ABTestAdminUi = lazy(() => import('@lucid/components').then(module => ({ default: module.ABTestAdminUi })));
const PingdomHydration = lazy(() => import('@lucid/components').then(module => ({ default: module.Pingdom })));

interface LayoutProps extends Lucid.Context.Layout, Lucid.Context.Blog.Context.Search {
  children: ReactNode;
  navbarProps?: NavbarOptionalProps;
  footerProps?: SiteFooterOptionalProps;
  hasNavbar?: boolean | null;
  hasFooter?: boolean | null;
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  shortCode,
  pageType,
  children,
  shouldHideSsr = false,
  nodeTranslations = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true,
  product,
  handleSearch,
  currentSearch,
  shouldDisplayBlogNav = false,
}) => {
  const { cmsUrl } = useSiteMetadata();
  const [shouldShowShield, setShouldShowShield] = useState(shouldHideSsr);
  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  const { locale_id, url_lang_prefix, name } = getLanguage(shortCode);
  const defaultPageUrls = useDefaultPageUrls(shortCode) as Record<string, string>;
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_footer_language_switch'],
    shortCode,
  });
  const announcement = useAnnouncementBanner(shortCode, product);
  const hasGlobalNav = useGlobalNav(pageType, product);

  const [abTests, updateTest] = useReducer((state: Lucid.ABTest.StateData, action: Lucid.ABTest.Action) => {
    const currentABTestPropKeys = Object.keys(state);

    if (!currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: action,
      };
    }

    if (currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: {
          ...state[action.testName],
          ...action,
        },
      };
    }

    return state;
  }, {});

  useEffect(() => {
    if (isBrowser()) {
      setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
      setShouldShowABTestButtons(window.localStorage.getItem('showAbTestingUi') === '1');
    }
  }, []);

  useEffect(() => {
    const mobileThreshold = 768;
    const mobileTimeout = 4000;
    const desktopTimeout = 1000;
    const timeoutInMs = window.innerWidth >= mobileThreshold ? desktopTimeout : mobileTimeout;

    const safetyTimeoutId = setTimeout(() => {
      setShouldShowShield(false);
    }, timeoutInMs);

    return () => {
      clearTimeout(safetyTimeoutId);
    };
  }, [shouldHideSsr]);

  const layoutContext = {
    shortCode,
    urlLangPrefix: url_lang_prefix,
    localeId: locale_id,
    product,
    pageType,
    abTesting: {
      updateTest,
    },
  };

  // Footer props
  const footerMenu = useNavFooterMenu(1, shortCode);

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <GlobalCSS />
      <Pingdom id="pingdom-check" />
      <Delayed>
        <Suspense fallback={<div key="pingdomHydrationFallback" id="pingdom-hydration" />}>
          {isBrowser() && (
            <PingdomHydration id="pingdom-hydration-check">PINGDOM_CANARY_STRING_HYDRATION</PingdomHydration>
          )}
        </Suspense>
      </Delayed>
      <ThemeProvider product={product} theme={theme}>
        <NavBlogProvider
          currentSearch={currentSearch}
          elasticEngine="coblog"
          handleSearch={handleSearch}
          shouldDisplayBlogNav={shouldDisplayBlogNav}
        >
          <ABTest.Shield shouldShowShield={shouldShowShield} />
          {hasNavbar && (
            // TODO FY26_LucidCoTryNowNavLink: Remove FY26_LucidCoTryNowNavLink ABTest
            <ABTest
              isAboveTheFold
              branchesToRender={['T_A', 'T_B']}
              css={theme => ({
                position: 'sticky',
                top: !hasGlobalNav ? 0 : '-48px',
                left: 0,
                zIndex: theme.zIndices.z2100,
              })}
              testName="FY26_LucidCoTryNowNavLink"
              willTriggerFire={shortCode === 'en' && product === 'co'}
            >
              <ABTest.Context.Consumer>
                {({ branch }) => (
                  <Navbar
                    {...navbarProps}
                    buttonAriaLabel={withSmartling('mktg_footer_language_switch', {
                      language: name,
                    })}
                    defaultPageUrls={defaultPageUrls}
                    globalMenuId={6}
                    hasGlobalNav={hasGlobalNav || shouldDisplayBlogNav}
                    nid={nid}
                    nodeTranslations={nodeTranslations}
                    pageType={pageType}
                    // TODO FY26_LucidCoTryNowNavLink: Remove shouldSkipTryNow ABTest
                    shouldSkipTryNow={branch === 'T_B'}
                    siteMenuId={{ scale: 5, spark: 38 }[product] ?? 1}
                  />
                )}
              </ABTest.Context.Consumer>
            </ABTest>
          )}
          <Box as="main" className="gatsby-root" data-lang={shortCode} data-nid={nid} data-page-type={pageType}>
            {announcement && <AnnouncementBanner announcement={announcement} />}
            {children}
            <Suspense fallback={<div key="cmsNodeSuspenseFallback" id="cmsnode-suspense-fallback" />}>
              {shouldShowEditButton && (
                <CMSNodeButton href={`${cmsUrl}/admin/content/${pageType.replace(/-/gi, '_')}/${nid}`} />
              )}
            </Suspense>
            <Suspense fallback={<div key="abTestsSuspenseFallback" id="abtests-suspense-fallback" />}>
              {shouldShowABTestButtons && !!Object.keys(abTests).length && <ABTestAdminUi abTests={abTests as any} />}
            </Suspense>
          </Box>
          {hasFooter && <SiteFooter {...footerProps} menu={footerMenu} nodeTranslations={nodeTranslations} />}
        </NavBlogProvider>
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default Layout;
